import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from "lucide-react"
import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from "react"
import { cn } from "@/lib/utils"

export const DEVICE_FLY_STICK_SIZE_PX = 72
const DEFAULT_DEADZONE_PX = 28

/** Bottom inset for left stick / Record (matches fly chrome). */
export const DEVICE_FLY_STICK_BOTTOM_CLASS = "bottom-[max(2.5rem,calc(env(safe-area-inset-bottom)+1.25rem))]"

/**
 * Modal phone chrome: clear path timeline (~h-8 + py + border) under the stage.
 * Fly mode hides the timeline — use DEVICE_FLY_STICK_BOTTOM_CLASS there.
 */
export const DEVICE_FLY_STICK_MODAL_BOTTOM_CLASS = "bottom-[max(5.5rem,calc(env(safe-area-inset-bottom)+3.75rem))]"

/** Horizontal inset for left/right stick and Record. */
export const DEVICE_FLY_STICK_LEFT_CLASS = "left-[max(2.25rem,calc(env(safe-area-inset-left)+1.25rem))]"
export const DEVICE_FLY_STICK_RIGHT_CLASS = "right-[max(2.25rem,calc(env(safe-area-inset-right)+1.25rem))]"

export type DeviceFlyStickProps = {
  side: "left" | "right"
  "aria-label": string
  className?: string
  onPress: () => void
  onMove: (delta: { dx: number; dy: number; radius: number }) => void
  onRelease: () => void
}

export function DeviceFlyStick({
  side,
  "aria-label": ariaLabel,
  className,
  onPress,
  onMove,
  onRelease,
}: DeviceFlyStickProps) {
  const originRef = useRef<{ x: number; y: number } | null>(null)
  const activeRef = useRef(false)

  const end = useCallback(() => {
    if (!activeRef.current) return
    activeRef.current = false
    originRef.current = null
    onRelease()
  }, [onRelease])

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (e.button !== 0) return
      e.preventDefault()
      e.stopPropagation()
      e.currentTarget.setPointerCapture(e.pointerId)
      activeRef.current = true
      originRef.current = { x: e.clientX, y: e.clientY }
      onPress()
    },
    [onPress],
  )

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLButtonElement>) => {
      if (!activeRef.current || !originRef.current) return
      const dx = e.clientX - originRef.current.x
      const dy = e.clientY - originRef.current.y
      onMove({ dx, dy, radius: DEVICE_FLY_STICK_SIZE_PX / 2 })
    },
    [onMove],
  )

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={cn(
        "pointer-events-auto relative touch-none rounded-full border border-white/25 bg-black/45 shadow-lg backdrop-blur-sm select-none",
        "[-webkit-touch-callout:none] active:bg-black/60",
        side === "left" ? DEVICE_FLY_STICK_LEFT_CLASS : DEVICE_FLY_STICK_RIGHT_CLASS,
        className,
      )}
      style={{ width: DEVICE_FLY_STICK_SIZE_PX, height: DEVICE_FLY_STICK_SIZE_PX }}
      {...{ "data-device-fly-stick": "" }}
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      onLostPointerCapture={end}
    >
      <ChevronUp
        aria-hidden
        className="pointer-events-none absolute top-1 left-1/2 size-3.5 -translate-x-1/2 text-white/55"
      />
      <ChevronDown
        aria-hidden
        className="pointer-events-none absolute bottom-1 left-1/2 size-3.5 -translate-x-1/2 text-white/55"
      />
      <ChevronLeft
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-1 size-3.5 -translate-y-1/2 text-white/55"
      />
      <ChevronRight
        aria-hidden
        className="pointer-events-none absolute top-1/2 right-1 size-3.5 -translate-y-1/2 text-white/55"
      />
    </button>
  )
}

export { DEFAULT_DEADZONE_PX as DEVICE_FLY_STICK_DEADZONE_PX }
